import { createBaseClass } from '@descope-ui/common/base-classes';
import {
  getComponentName,
  injectStyle
} from '@descope-ui/common/components-helpers';
import {
  componentNameValidationMixin,
  createStyleMixin,
  draggableMixin,
} from '@descope-ui/common/components-mixins';
import { compose } from '@descope-ui/common/utils';

export const componentName = getComponentName('password-strength');
class RawPasswordStrength extends createBaseClass({
  componentName,
  baseSelector: ':host > .wrapper',
}) {
  static get observedAttributes() {
    return ['options', 'value'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
      <div class="wrapper">
        <div class="bars-container"></div>
        <div class="text-container">
        </div>
      </div>
		`;

    injectStyle(`
        :host {
					display: inline-flex;
				}

        .wrapper {
          display: inline-flex;
          position: relative;
          flex-direction: column;
          width: 100%;
          height: 100%;
          align-items: flex-start;
        }

        .bar {
          flex-grow: 1;
        }

        .bars-container {
          display: flex;
          flex-grow: 1;
          width: 100%;
        }
    `, this);

    this.barsContainer = this.shadowRoot.querySelector('.bars-container');
    this.textContainer = this.shadowRoot.querySelector('.text-container');
  }

  get options() {
    return this.getAttribute('options')?.split(',') || [];
  }

  // we need to have the option to take the zxcvbn from outside the component
  getCalcScoreFn = () => import('./calcScore').then(module => module.default);

  #score = -1;

  async calcScore() {
    const value = this.getAttribute('value') || ''
    if (!value) return -1;

    const calcScore = await this.getCalcScoreFn();

    return calcScore(value).score;
  }

  renderBars() {
    this.barsContainer.innerHTML = '';
    this.bars = this.options.map((option, index) => {
      const bar = document.createElement('div');
      bar.classList.add('bar');
      bar.classList.toggle('filled', index <= this.#score);
      this.barsContainer.appendChild(bar);

      return bar;
    });
  }

  init() {
    super.init?.();
    // we are prefetching the calcScore function
    this.getCalcScoreFn();
  }


  async onValueChange() {
    const newScore = await this.calcScore();
    if (newScore === this.#score) return;

    this.#score = newScore;
    this.dispatchEvent(new CustomEvent('score-changed', { bubbles: true, composed: true, detail: this.#score }));

    this.bars?.forEach((bar, index) => {
      bar.classList.toggle('filled', index <= this.#score);
    });

    this.textContainer.innerHTML = this.options[this.#score] || '';

    if (this.#score === -1) this.removeAttribute('score');
    else this.setAttribute('score', this.#score);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'options') {
      return this.renderBars();
    }

    if (name === 'value') {
      return this.onValueChange();
    }
  }
}

const { host, bar, filledBar, barsContainer, text } = {
  host: { selector: () => ':host' },
  bar: { selector: ' .bar' },
  barsContainer: { selector: ' .bars-container' },
  filledBar: { selector: ' .bar.filled' },
  text: { selector: ' .text-container' },
};

export const PasswordStrengthClass = compose(
  createStyleMixin({ componentNameOverride: getComponentName('input-wrapper') }),
  createStyleMixin({
    mappings: {
      hostWidth: [
        { ...host, property: 'width' },
        { ...host, property: 'min-width' },
      ],
      hostDirection: { ...host, property: 'direction' },
      hostMinWidth: { ...host, property: 'min-width' },
      barHeight: { ...bar, property: 'height' },
      barBorderRadius: { ...bar, property: 'border-radius' },
      emptyBarColor: { ...bar, property: 'background-color' },
      filledBarColor: { ...filledBar, property: 'background-color' },
      textColor: { ...text, property: 'color' },
      spacing: { property: 'gap' },
      barSpacing: { ...barsContainer, property: 'gap' },
      fontSize: {},
      fontWeight: {},
      fontFamily: {},
      barTransition: { ...bar, property: 'transition' },
    },
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawPasswordStrength);
